import { Font } from '@react-pdf/renderer';
import { isEmpty } from 'lodash';

import { asyncIterator } from '@src/utils/helperFunctions';

// const register = ()=> {}

type FontStyle = 'normal' | 'italic' | 'oblique';

type FontWeight =
  | number
  | 'thin'
  | 'ultralight'
  | 'light'
  | 'normal'
  | 'medium'
  | 'semibold'
  | 'bold'
  | 'ultrabold'
  | 'heavy';

type Fonts = {
  src: string
  fontWeight: FontWeight
  fontStyle?: FontStyle
};

type Config = {
  family: string
  fonts: Fonts[]
};

const fontsConfig: Record<string, Fonts[]> = {
  Inter: [
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyeMZhrib2Bg-4.ttf',
      fontWeight: 100
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuDyfMZhrib2Bg-4.ttf',
      fontWeight: 200
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuOKfMZhrib2Bg-4.ttf',
      fontWeight: 300
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfMZhrib2Bg-4.ttf',
      fontWeight: 400
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuI6fMZhrib2Bg-4.ttf',
      fontWeight: 500
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYMZhrib2Bg-4.ttf',
      fontWeight: 600
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYMZhrib2Bg-4.ttf',
      fontWeight: 700
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuDyYMZhrib2Bg-4.ttf',
      fontWeight: 800
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuBWYMZhrib2Bg-4.ttf',
      fontWeight: 900
    }
  ]
};

const registerCustomPdfFonts = () => {
  const validFonts: Config[] = Object.keys(fontsConfig).map((family) => ({
    family,
    fonts: []
  }));

  const fetchFont = async (font: Fonts, config: Config) => {
    try {
      await fetch(font.src);
      config.fonts.push(font);
    } catch (err: any) {
      console.error('Problem registering font:', err);
    }
  };

  asyncIterator(validFonts, async (config) => {
    await Promise.all(fontsConfig[config.family].map(async (font) => {
      await fetchFont(font, config);
    }));
  }).then(() => {
    validFonts.filter((font) => !isEmpty(font.fonts))
      .map(({ fonts, family }) => Font.register({
        family,
        fonts
      }));
  })
    .catch((err) => {
      console.error(err);
    });
};

const cachedRequests = new Map<string, boolean>();

export const maybeCustomPdfFont = (font: string, fallback = 'Helvetica') => {
  try {
    if (cachedRequests.has(font)) {
      if (cachedRequests.get(font)) {
        return font;
      }
      return fallback;
    }

    const requestedFont = Font.getFont({ fontFamily: font });
    if (requestedFont?.src) {
      cachedRequests.set(font, true);
    }
    return font;
  } catch {
    cachedRequests.set(font, false);
    return fallback;
  }
};

export default registerCustomPdfFonts;
